import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import activeAdsIcon from "../../assets/images/icons/ads-active-ads.svg";
import inReviewIcon from "../../assets/images/icons/ads-in-review.svg";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { toast } from "react-toastify";
import { getAllMyCampaigns } from "../../hook/useAdsApi";
import {
  AD_STATUS,
  AD_STATUS_FILTER_TABS,
  adMatchesFilterTab,
  getAdDisplayLabel,
  statusBadgeClasses,
  statusCardClasses,
} from "./adsConstants";

function daysRemaining(endDate) {
  if (!endDate) return null;
  const diffMs = new Date(endDate).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function getFirstName() {
  try {
    const user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user"));
    return user?.first_name || user?.username || "";
  } catch {
    return "";
  }
}

export default function AdCampaignManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState(AD_STATUS_FILTER_TABS[0].label);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAllMyCampaigns()
      .then((result) => {
        if (!cancelled) setCampaigns(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load your ad campaigns", { toastId: "ad-campaigns-load-error" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The filter tabs (Live/In Review/Rejected) are ad-level statuses, so the dashboard list
  // is one card per Ad, not per Campaign - flatten what getAllMyCampaigns() fetched.
  const allAds = useMemo(
    () => campaigns.flatMap((campaign) => (campaign.ads || []).map((ad) => ({ ...ad, campaign }))),
    [campaigns]
  );

  const activeCount = allAds.filter((ad) => ad.status === AD_STATUS.ACTIVE || ad.status === AD_STATUS.APPROVED).length;
  const inReviewCount = allAds.filter((ad) => ad.status === AD_STATUS.SUBMITTED).length;

  const filteredAds = useMemo(() => {
    const tab = AD_STATUS_FILTER_TABS.find((t) => t.label === activeTab);
    return allAds.filter((ad) => adMatchesFilterTab(ad.status, tab?.value ?? null));
  }, [allAds, activeTab]);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {greeting()}{getFirstName() ? `, ${getFirstName()}.` : "."}
                </h1>
                <p className="text-sm text-gray-500 mt-1">Here's a quick overview of your ad campaign performance.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/ads/select-account")}
                className="bg-[#095228] text-white font-semibold px-5 py-2 rounded-full text-sm hover:opacity-90 transition"
              >
                + Create Ad Campaign
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <>
                {/* Exact colors/assets from the Figma "Ad Creation & Review_Web" section (read-only access). */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="rounded-[32px] py-4 px-6 bg-[#fefdeb] flex items-center gap-3">
                    <img src={activeAdsIcon} alt="" className="w-[65px] h-[65px] shrink-0" />
                    <p className="whitespace-nowrap">
                      <span className="text-[#00092c] font-black text-[32px]">{activeCount} </span>
                      <span className="text-[#00092c] font-bold text-lg">Active Ads</span>
                    </p>
                  </div>
                  <div className="rounded-[32px] py-4 px-6 bg-[#ebf8ed] flex items-center gap-3">
                    <img src={inReviewIcon} alt="" className="w-[65px] h-[65px] shrink-0" />
                    <p className="whitespace-nowrap">
                      <span className="text-[#00092c] font-black text-[32px]">{String(inReviewCount).padStart(2, "0")} </span>
                      <span className="text-[#095228] font-bold text-lg">In Review</span>
                    </p>
                  </div>
                </div>

                {/* Spend tracking isn't stored on any ad/campaign/ad-set model yet -
                    there is no backing endpoint for this figure, shown as 0 until that exists. */}
                <div
                  className="rounded-[26px] p-4 mb-6 text-white"
                  style={{ backgroundImage: "linear-gradient(256deg, #1de78b 2.6%, #08aeea 97.5%)" }}
                >
                  <p className="text-sm font-bold">7-Day Spend</p>
                  <p className="text-4xl font-semibold mt-2">$ 0.00</p>
                  <p className="text-xs font-semibold text-[#161f28] mt-1">Spend tracking coming soon</p>
                </div>

                <h2 className="text-lg font-semibold mb-2 pb-2 border-b border-[#c3c5d9] text-[#131b2e]">
                  Your Ad Campaigns
                </h2>
                <div className="flex gap-1.5 mb-4 bg-[#ebf8ed] rounded-full p-1 w-fit">
                  {AD_STATUS_FILTER_TABS.map((tab) => (
                    <button
                      key={tab.label}
                      type="button"
                      onClick={() => setActiveTab(tab.label)}
                      className={`px-5 py-1.5 rounded-full text-xs font-bold ${
                        activeTab === tab.label ? "bg-[#095228] text-white" : "text-[#095228]"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  {filteredAds.length === 0 && (
                    <p className="text-gray-500 text-sm">No ads found for this filter.</p>
                  )}
                  {filteredAds.map((ad) => {
                    const remaining = daysRemaining(ad.campaign?.end_date);
                    const thumbnail = ad.thumbnail?.url || ad.creative_media?.[0]?.url;
                    const budget = Number(ad.campaign?.budget) || 0;
                    const isLive = ad.status === AD_STATUS.ACTIVE || ad.status === AD_STATUS.APPROVED;
                    const isRejected = ad.status === AD_STATUS.REJECTED;
                    return (
                      <div
                        key={ad.id}
                        onClick={() => navigate(`/ads/ads/${ad.id}`)}
                        className={`rounded-[22px] border p-4 cursor-pointer hover:shadow-md transition ${statusCardClasses(ad.status)}`}
                      >
                        {thumbnail && (
                          <img
                            src={thumbnail}
                            alt=""
                            className={`w-full h-20 object-cover rounded-[20px] mb-3 ${isRejected ? "opacity-60" : ""}`}
                          />
                        )}
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${statusBadgeClasses(ad.status)}`}>
                              {getAdDisplayLabel(ad.status)}
                            </span>
                            <span className="text-sm font-bold text-[#737688]">ID: AD-{ad.id}</span>
                          </div>
                          {isLive && remaining !== null && remaining >= 0 && (
                            <span className="text-[10px] font-bold uppercase bg-[#74131c] text-[#f9e9ea] px-2 py-0.5 rounded-full">
                              Ends in {remaining} {remaining === 1 ? "day" : "days"}
                            </span>
                          )}
                        </div>
                        <p className="font-semibold text-[#131b2e] text-lg">{ad.name}</p>
                        {ad.campaign?.id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/ads/campaigns/${ad.campaign.id}`);
                            }}
                            className="text-xs font-semibold text-[#095228] underline mb-2"
                          >
                            Campaign ID: CMP-{ad.campaign.id}
                          </button>
                        )}

                        {isRejected ? (
                          <p className="text-sm font-bold text-[#ba1a1a]">
                            {ad.rejection_reason || "Policy violation"}
                          </p>
                        ) : (
                          budget > 0 && (
                            <div className="flex items-center gap-3 mt-1">
                              {/* No spend-tracking column exists yet - progress is always 0,
                                  shown honestly rather than a fabricated fill. */}
                              <div className="h-2.5 flex-1 max-w-[200px] rounded-full bg-[#d9d9d9] overflow-hidden">
                                <div className="h-full bg-[#095228] rounded-full" style={{ width: "0%" }} />
                              </div>
                              <p className="text-xs font-semibold whitespace-nowrap">
                                <span className="text-[#be1f2e]">$0</span>
                                <span className="text-[#434656]"> / ${budget}</span>
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
